import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Legend, Scatter, ResponsiveContainer } from 'recharts';
import { useState, useEffect } from 'react';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import TranslationText from '@Components/Utilities/TranslationText';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilState } from 'recoil';
import translator from '@Components/Utilities/Translator';

const auctionColor = '#F28C28';
const retailColor = '#808080';
const retailLineColor = '#3b3a39';
const auctionLineColor = '#b55b02';


const MarketAnalysisChart = (props) => {

    const [dataLine, setDataLine] = useState([]);
    const [timeSelected, setTimeSelected] = useState('6 Months');
    const windowSize = useWindowSize();
    const [loadingChart, setLoadingChart] = useState(true);
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);

    useEffect(() => {
        setDataLine(sortLogic());
    }, [props.Auction || props.Retail]);

    const sortLogic = () => {
        const lineData = [];

        for (let i = 0; i < props.RetailData?.length; i++) {
            lineData.push({
                retail: runRetailData(props.RetailData[i].Price),
                date: new Date(props.RetailData[i].SaleDate)
            });
        }
        for (let i = 0; i < props.AuctionData?.length; i++) {
            lineData.push({
                auction: runRetailData(props.AuctionData[i].Price),
                date: new Date(props.AuctionData[i].SaleDate)
            });
        }

        for (let i = 0; i < props.RetailLineData?.length; i++) {
            let myDate = new Date(props.RetailLineData[i].SaleDate);
            lineData.push({
                rl: props.RetailLineData[i].Price,
                date: myDate
            });
        }
        for (let i = 0; i < props.AuctionLineData?.length; i++) {
            let myDate = new Date(props.RetailLineData[i]?.SaleDate || props.AuctionLineData[i].SaleDate);
            lineData.push({
                al: props.AuctionLineData[i].Price,
                date: myDate
            });
        }

        let sortedData = lineData.sort(function (a, b) { return a.date - b.date }).map((obj) => ({ ...obj, date: obj.date.toLocaleDateString() }));
        if (lineData.length == 0) {
            setLoadingChart(false);
        }

        return sortedData;
    };

    const runRetailData = (data) => {
        let x = currentUser.XYValues[0];
        let y = currentUser.XYValues[1];

        data = (data*(y*y))-(3*x);

        return data;
    }


    const TimeSpanChanged = (timeSpan) => {
        setTimeSelected(timeSpan.DisplayName);
        setLoadingChart(true);
        props.TimeChanged(timeSpan);
    };

    const renderCustomXAxis = (props) => {
        const { x, y, payload } = props;

        return (
            <g transform={`translate(${x},${y})`}>
                <text fontSize={8} x={0} y={0} dy={16} textAnchor="end" transform="rotate(-30)">{new Date(payload.value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</text>
            </g>
        );
    };


    return (
        <div className='full-container'>
            {loadingChart && <LoadingRing sxBox={{ 'justify-content': 'center', 'height': '50px', 'margin-top': '20px', 'margin-bottom': '20px' }} />}
            <div className={loadingChart ? 'hidden' : ''}>
                <div className='analysis-time-buttons'>
                    {props.TimeSpansAvailable.map((item) => {
                        return (
                            <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); TimeSpanChanged(item); } }} className={item.DisplayName === timeSelected ? 'ts-button time-analysis-button active' : 'ts-button time-analysis-button'} key={item.DisplayName} onClick={() => TimeSpanChanged(item)}>
                                {item.DisplayName}
                            </div>
                        );
                    })
                    }
                </div>
                {dataLine && dataLine.length > 0 ?
                    <ResponsiveContainer width={windowSize.width < 1000 ? windowSize.width < 400 ? 370 : 400 : 1100} aspect={2} margin={{ left: 70, bottom: 50, top: 30 }} >
                        <ComposedChart
                            width={windowSize.width < 1000 ? windowSize.width < 400 ? 325 : 350 : 1000}
                            height={windowSize.isMobile ? 200 : 400}
                            data={dataLine}
                            margin={{
                                top: 20,
                                right: windowSize.isMobile ? 5 : 80,
                                bottom: 20,
                                left: windowSize.isMobile ? 0 : 50,
                            }}
                        >
                            <CartesianGrid stroke="#f5f5f5" />
                            <Legend
                                margin={{
                                    top: 50
                                }}
                            />

                            <XAxis dataKey="date" height={70} tick={renderCustomXAxis} domain={[0, 'auto']} allowDuplicatedCategory={false} />
                            <YAxis type="number" width={70} tickFormatter={(val) => {
                                if (parseInt(val) >= 1000) {
                                    return '$' + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                                } else {
                                    return '$' + val;
                                }; }} tick={{ fontSize: 10 }} />
                            <Scatter name="Auction" isAnimationActive={false} dataKey='auction' fill={auctionColor} />
                            <Scatter name="Retail" isAnimationActive={false} dataKey='retail' fill={retailColor} />
                            <Line dataKey='al' strokeWidth={3} connectNulls dot={false} stroke={auctionLineColor} onAnimationEnd={(e) => { setLoadingChart(false) }} legendType='none' />
                            <Line dataKey='rl' isAnimationActive={false} strokeWidth={3} connectNulls dot={false} stroke={retailLineColor} legendType='none' />
                        </ComposedChart>
                    </ResponsiveContainer>
                    :
                    <div className='no-data'>
                        <TranslationText tKey='sandhills.tradesites.mvc.nodataavailable' />
                    </div>
                }

                <table id='high-low-average-table'>
                    <thead>
                        <tr>
                            <td id='description-column'></td>
                            {windowSize.isMobile 
                                ? <> <th><TranslationText tKey='sandhills.tradepubwebsite.mvc.auction' /></th>
                                    <th><TranslationText tKey='sandhills.tradepubwebsite.mvc.retail' /></th></>
                                : <><th><TranslationText tKey='sandhills.tradepubwebsite.mvc.auctionresults'/></th>
                                    <th><TranslationText tKey='sandhills.tradesites.mvc.retailresults'/></th></>}

                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><TranslationText tKey='sandhills.tradepubwebsite.mvc.totalnumberoflistingsfound' /></td>
                            <td><a id='hi-lo-avg-auction-link' href={props.AuctionPageURL}>{props.Auction.Count}</a></td>
                            <td>{props.DisableRetailLink ? <>{props.Retail.Count}</> : <a id='hi-lo-avg-retail-link' href={props.ListingPageURL} target='_blank' aria-label={props.Retail.Count + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>{props.Retail.Count}</a>}</td>
                        </tr>
                        <tr>
                            <td><TranslationText tKey='sandhills.tradepubwebsite.mvc.highestpricefound' /></td>
                            <td>{props.Auction.MaximumPrice}</td>
                            <td>{props.Retail.MaximumPrice}</td>
                        </tr>
                        <tr>
                            <td><TranslationText tKey='sandhills.tradepubwebsite.mvc.lowestpricefound' /></td>
                            <td>{props.Auction.MinimumPrice}</td>
                            <td>{props.Retail.MinimumPrice}</td>
                        </tr>
                        <tr>
                            <td><strong><TranslationText tKey='sandhills.tradepubwebsite.mvc.averageprice' /></strong></td>
                            <td><strong>{props.Auction.AveragePrice}</strong></td>
                            <td><strong>{props.Retail.AveragePrice}</strong></td>
                        </tr>
                        <tr>
                            <td><strong><TranslationText tKey='sandhills.avgwohighlow' /></strong></td>
                            <td><strong>{props.Auction.AdjustedAveragePrice}</strong></td>
                            <td><strong>{props.Retail.AdjustedAveragePrice}</strong></td>
                        </tr>
                    </tbody>
                </table>
                <div className='hilow-average-blurb'>
                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.highlowexplanation1' />&nbsp;<TranslationText tKey='sandhills.tradepubwebsite.mvc.highlowexplanation2' />&nbsp;<TranslationText tKey='sandhills.tradepubwebsite.mvc.highlowexplanation3' />&nbsp;<TranslationText tKey='sandhills.tradepubwebsite.mvc.highlowexplanation4' />&nbsp;<TranslationText tKey='sandhills.tradepubwebsite.mvc.highlowexplanation5' />
                </div>
            </div>
        </div>
    )
};

export default MarketAnalysisChart;